import { useEffect, useMemo, useState } from 'react';

import { handleError } from 'utils/errorHandler';
import { getEmployeeSelectOptions } from 'utils/user';
import { checkStringLength, isStringEmpty } from 'utils/string';

import { useDebounce } from './useDebounce';
import * as userService from 'services/user';
import { MAKE_SEARCH_BY } from 'constants/fetchTypes';
import { MIN_USER_SEARCH_LENGTH } from 'constants/appConstants';
import { DebounceOptions } from 'pages/settings/users/userPermissions/types';
import type { QueryObject } from 'interface/queryParam';
import type { EmployeeLike } from 'interface/userUtils';

type EmployeeSelectOption = ReturnType<typeof getEmployeeSelectOptions>;

type FetchAllEmployeesResponse = {
  data: EmployeeLike[];
};

const EMPTY_FETCH_PROPS: QueryObject = Object.freeze({});

const useEmployeeDebounceOptions = (
  employeeId?: string | number,
  additionalFetchProps?: QueryObject,
  searchUserByFullName = false
): DebounceOptions => {
  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState<EmployeeSelectOption[]>([]);
  const [code, debounceFunction] = useDebounce();

  const rawAdditionalFetchProps = additionalFetchProps ?? EMPTY_FETCH_PROPS;
  const additionalFetchPropsKey = JSON.stringify(rawAdditionalFetchProps);
  const stableAdditionalFetchProps = useMemo(
    () => JSON.parse(additionalFetchPropsKey) as QueryObject,
    [additionalFetchPropsKey]
  );

  useEffect(() => {
    (async function fetchEmployee() {
      if (!employeeId && checkStringLength(code, MIN_USER_SEARCH_LENGTH)) {
        return;
      }

      try {
        setIsLoading(true);

        const params: QueryObject = {
          q: code,
          fields: 'id,empId,firstName,lastName,middleName',
          ...stableAdditionalFetchProps,
        };

        if (employeeId && isStringEmpty(code)) {
          const data = (await userService.fetchById(employeeId)) as EmployeeLike;

          setOptions([data].map(getEmployeeSelectOptions));

          return;
        }

        if (!searchUserByFullName) {
          params.searchBy = MAKE_SEARCH_BY.join(',');
        }

        const { data } = (await userService.fetchAll(params)) as FetchAllEmployeesResponse;

        setOptions(data.map(getEmployeeSelectOptions));
      } catch (error) {
        handleError(error);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [code, employeeId, searchUserByFullName, stableAdditionalFetchProps]);

  return [isLoading, options, code, debounceFunction];
};

export default useEmployeeDebounceOptions;
